import { Flex, Text, Tooltip } from "@radix-ui/themes";
import { Link } from "@tanstack/react-router";
import { format, parseISO } from "date-fns";

import ArrowRightIcon from "@/assets/icons/arrow right.svg?react";
import InfoIcon from "@/assets/icons/Info outlined.svg?react";
import { useLoggedInUser } from "@/components/providers/ApiProvider/hooks";
import { useTracksControllerUnlockUsage } from "@/lib/lutely-api/gen/tracks/tracks";

const PLAN_LABELS: Record<string, string> = {
  creator: "Creator",
  label: "Label",
  pro: "Pro",
  flex: "Flex",
};

function formatResetDate(periodEnd: string | Date | undefined) {
  if (!periodEnd) return null;
  try {
    const date =
      typeof periodEnd === "string" ? parseISO(periodEnd) : new Date(periodEnd);
    return format(date, "MMM d, yyyy");
  } catch {
    return null;
  }
}

/**
 * Compact unlocks meter for the Audiolab sidebar: usage, when the allowance
 * resets, the current plan, and an upgrade path. Reads the same query as the
 * unlock modal, so unlocking refreshes it in place with no reload.
 */
export default function SidebarUnlockTracker() {
  const { subscription, hasActiveSubscription, isLabelSubscriber, isLoading } =
    useLoggedInUser();
  const { data } = useTracksControllerUnlockUsage({
    query: { enabled: hasActiveSubscription },
  });

  if (isLoading || !hasActiveSubscription || !data) {
    return null;
  }

  const resetDate = formatResetDate(data.periodEnd);
  const planLabel = subscription?.subscriptionTier
    ? (PLAN_LABELS[subscription.subscriptionTier] ??
      subscription.subscriptionTier)
    : null;
  const isLabel = isLabelSubscriber;
  const percentUsed =
    data.included > 0
      ? Math.min(100, Math.round((data.used / data.included) * 100))
      : 0;

  return (
    <Flex
      direction="column"
      className="gap-[6px] pb-[16px]"
      data-testid="sidebar-unlock-tracker"
    >
      <Flex className="items-center gap-[4px]">
        <Text
          className="text-[14px] font-semibold text-text-primary"
          data-testid="sidebar-unlock-tracker-count"
        >
          {data.used}/{data.included}
        </Text>
        <Text className="text-[13px] text-text-secondary">Unlocks used</Text>
        <Tooltip
          content={
            <Flex direction="column" className="gap-[10px] text-[14px]">
              <Text>Limits refresh every month with your subscription.</Text>
              <Text>Unused unlocks do not roll over.</Text>
              <Text>
                Need more? Upgrade your plan or pay-as-you-go until your plan
                renews.
              </Text>
            </Flex>
          }
        >
          <InfoIcon width={14} height={14} />
        </Tooltip>
      </Flex>

      <div
        className="h-[6px] w-full overflow-hidden rounded-full bg-background-quarternary"
        role="progressbar"
        aria-valuenow={data.used}
        aria-valuemin={0}
        aria-valuemax={data.included}
        aria-label="Unlocks used this period"
      >
        <div
          className="h-full rounded-full bg-brand-primary transition-all"
          style={{ width: `${percentUsed}%` }}
        />
      </div>

      {resetDate && (
        <Text className="text-[12px] text-text-secondary">
          Resets on {resetDate}
        </Text>
      )}
      {planLabel && (
        <Text className="text-[12px] text-text-secondary">
          Plan: {planLabel}
        </Text>
      )}

      {!isLabel && (
        <Link
          to="/upgrade"
          className="flex items-center gap-[4px] text-[12px] font-semibold text-brand-primary no-underline"
        >
          Upgrade plan
          <ArrowRightIcon width={12} height={12} />
        </Link>
      )}
    </Flex>
  );
}
